import { useLocation, NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Wallet,
  CalendarClock,
  Users,
  GraduationCap,
  Megaphone,
  UserSquare2,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  BarChart3,
  MessageSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import { usePermissions, type ModuleKey } from '@/hooks/usePermissions';
import { useUser } from '@/hooks/useUser';
import { hasPermission, type FeatureKey } from '@/lib/permissions';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface NavSubItem {
  to: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Sprint 8.2 — Feature key requise pour afficher ce sous-item. */
  feature?: FeatureKey;
}

interface NavItem {
  to: string;
  module: ModuleKey;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  section: 'pilotage' | 'operations' | 'commercial' | 'admin';
  /** Sprint 6.9.1 — sous-items expandables (uniquement Finances pour l'instant) */
  children?: NavSubItem[];
  /** Préfixe URL qui marque cet item "actif" (pour expand auto). Default = `to`. */
  activePrefix?: string;
}

const NAV_ITEMS: NavItem[] = [
  // PILOTAGE
  {
    to: '/',
    module: 'dashboard',
    icon: LayoutDashboard,
    label: t('nav.dashboard'),
    section: 'pilotage',
  },
  // OPÉRATIONS
  {
    to: '/finances/caisse/operations',
    module: 'finances',
    icon: Wallet,
    label: 'Finances',
    section: 'operations',
    activePrefix: '/finances',
    // Sprint 6.9.1 — sous-menu expandable (Caisse + Consolidés)
    children: [
      { to: '/finances/caisse/operations', label: 'Caisse', feature: 'caisse.view' },
      {
        to: '/finances/consolides/formateurs',
        label: 'Consolidé Formateurs',
        feature: 'consolide_formateurs.view',
      },
      {
        to: '/finances/consolides/fournisseurs',
        label: 'Dettes Fournisseurs',
        feature: 'dettes_fournisseurs.view',
      },
      {
        to: '/finances/consolides/recouvrement',
        label: 'Recouvrement',
        feature: 'recouvrement.view',
      },
    ],
  },
  {
    to: '/echeances',
    module: 'echeances',
    icon: CalendarClock,
    label: t('nav.echeances'),
    section: 'operations',
  },
  // COMMERCIAL
  { to: '/crm', module: 'crm', icon: Users, label: t('nav.crm'), section: 'commercial' },
  {
    to: '/formation',
    module: 'formation',
    icon: GraduationCap,
    label: 'Formation',
    section: 'commercial',
  },
  {
    to: '/marketing',
    module: 'marketing',
    icon: Megaphone,
    label: 'Marketing Digital',
    section: 'commercial',
  },
  // ADMINISTRATION
  {
    to: '/reporting',
    module: 'dashboard',
    icon: BarChart3,
    label: 'Reporting',
    section: 'admin',
  },
  { to: '/rh', module: 'rh', icon: UserSquare2, label: 'Ressources Humaines', section: 'admin' },
  {
    to: '/communication',
    module: 'marketing',
    icon: MessageSquare,
    label: 'Communication',
    section: 'admin',
  },
  {
    to: '/parametres',
    module: 'parametres',
    icon: Settings,
    label: t('nav.parametres'),
    section: 'admin',
  },
];

const SECTION_LABELS = {
  pilotage: t('nav.sectionPilotage'),
  operations: t('nav.sectionOperations'),
  commercial: t('nav.sectionCommercial'),
  admin: t('nav.sectionAdministration'),
} as const;

export function Sidebar({ collapsed, onToggle }: SidebarProps): JSX.Element {
  const { canAccess, loading } = usePermissions();
  const { metadata } = useUser();
  const isAdmin = metadata?.role === 'super_admin' || metadata?.role === 'direction';
  const customPermissions =
    (metadata as unknown as { permissions?: Record<string, boolean> | null } | null)?.permissions ??
    null;
  const canAccessFeature = (key: FeatureKey | undefined): boolean =>
    key == null ? true : hasPermission(customPermissions, key, isAdmin);
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<Set<string>>(() => new Set());

  // Sprint 6.9.1 — auto-expand le menu si l'URL match son activePrefix
  useEffect(() => {
    setOpenMenus((prev) => {
      const next = new Set(prev);
      for (const item of NAV_ITEMS) {
        if (item.children && location.pathname.startsWith(item.activePrefix ?? item.to)) {
          next.add(item.to);
        }
      }
      return next;
    });
  }, [location.pathname]);

  const visibleItems = loading ? NAV_ITEMS : NAV_ITEMS.filter((item) => canAccess(item.module));
  const sections = ['pilotage', 'operations', 'commercial', 'admin'] as const;

  const toggleMenu = (key: string): void => {
    setOpenMenus((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <aside
      className={cn(
        'flex h-screen flex-col bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-3">
        <div className="flex items-center gap-2 overflow-hidden">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">
            K
          </div>
          {!collapsed && (
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-wide text-white">KOST ERP</div>
              <div className="text-[10px] text-sidebar-foreground/70">Pilotage groupe</div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="rounded-md p-1 text-sidebar-foreground/70 hover:bg-sidebar-border/40 hover:text-white"
          aria-label={collapsed ? 'Développer la sidebar' : 'Replier la sidebar'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      <nav className="scrollbar-thin flex-1 space-y-4 overflow-y-auto px-2 py-4">
        {sections.map((section) => {
          const items = visibleItems.filter((i) => i.section === section);
          if (items.length === 0) return null;
          return (
            <div key={section}>
              {!collapsed && (
                <div className="px-2 pb-1.5 text-[10px] font-semibold tracking-[0.12em] text-sidebar-foreground/50">
                  {SECTION_LABELS[section]}
                </div>
              )}
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const hasChildren = (item.children?.length ?? 0) > 0;
                  const isOpen = openMenus.has(item.to);
                  const matchPrefix = location.pathname.startsWith(item.activePrefix ?? item.to);
                  // Si collapsed OU pas d'enfants → NavLink direct (comportement legacy).
                  if (!hasChildren || collapsed) {
                    return (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.to === '/'}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                              isActive || (collapsed && hasChildren && matchPrefix)
                                ? 'bg-sidebar-accent/15 text-white'
                                : 'text-sidebar-foreground/80 hover:bg-sidebar-border/40 hover:text-white',
                              collapsed && 'justify-center',
                            )
                          }
                          title={collapsed ? item.label : undefined}
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          {!collapsed && <span className="truncate">{item.label}</span>}
                        </NavLink>
                      </li>
                    );
                  }
                  // Sprint 6.9.1 — bouton expandable + sous-items
                  return (
                    <li key={item.to}>
                      <button
                        type="button"
                        onClick={() => toggleMenu(item.to)}
                        aria-expanded={isOpen}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                          matchPrefix
                            ? 'bg-sidebar-accent/15 text-white'
                            : 'text-sidebar-foreground/80 hover:bg-sidebar-border/40 hover:text-white',
                        )}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="flex-1 truncate text-left">{item.label}</span>
                        <ChevronDown
                          className={cn(
                            'h-3.5 w-3.5 shrink-0 text-sidebar-foreground/60 transition-transform',
                            isOpen && 'rotate-180',
                          )}
                        />
                      </button>
                      {isOpen && (
                        <ul className="ml-7 mt-0.5 space-y-0.5 border-l border-sidebar-border/40 pl-2">
                          {(item.children ?? [])
                            .filter((sub) => canAccessFeature(sub.feature))
                            .map((sub) => (
                              <li key={sub.to}>
                                <NavLink
                                  to={sub.to}
                                  className={({ isActive }) =>
                                    cn(
                                      'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                                      isActive
                                        ? 'bg-sidebar-accent/15 text-white'
                                        : 'text-sidebar-foreground/70 hover:bg-sidebar-border/40 hover:text-white',
                                    )
                                  }
                                >
                                  <span className="truncate">{sub.label}</span>
                                </NavLink>
                              </li>
                            ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="border-t border-sidebar-border px-3 py-3 text-[10px] text-sidebar-foreground/50">
          v0.0.1 · Sprint 0
        </div>
      )}
    </aside>
  );
}
